import React, { useMemo } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';

import { formatPrice } from '@dashboard/services/formatting/price';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useCouponQueries } from '@dashboard/hooks/queries/useCouponQueries';
import { couponsKey } from '@dashboard/lib/queryKeys';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';
import { getStatusFromQuery } from '@dashboard/lib/tableHelpers';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import Button from '@dashboard/components/Button';
import Dash from '@dashboard/components/Dash';
import CouponCode from '@dashboard/components/DiscountsCoupons/CouponCode';
import EmptyState from '@dashboard/components/EmptyState';
import PageHeader from '@dashboard/components/PageHeader';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import CouponStatusBadge from '@/dashboard/components/DiscountsCoupons/CouponStatusBadge';
import { formatDateTime } from '@/dashboard/lib/format-date';

const FILTER_OPTIONS = {
  ALL: {
    value: 'all',
    label: 'All',
  },
  ACTIVE: {
    value: 'active',
    label: 'Active',
  },
  EXPIRED: {
    value: 'expired',
    label: 'Expired',
  },
};

function getCouponColumns() {
  return [
    {
      accessorKey: 'label',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Label" />,
      cell: ({ row }) => {
        const coupon = row.original;
        return <span className="font-medium">{coupon.label || <Dash />}</span>;
      },
      enableSorting: true,
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) =>
        row.original.code ? <CouponCode code={row.original.code} copy /> : <Dash />,
      enableSorting: false,
      meta: { className: 'whitespace-nowrap' },
    },
    {
      id: 'discount',
      header: 'Discount',
      cell: ({ row }) => {
        const coupon = row.original;
        return coupon.amountOff
          ? formatPrice({
              amount: coupon.amountOff,
              currency: coupon.currency,
            })
          : `${coupon.percentOff}%`;
      },
      enableSorting: false,
    },
    {
      accessorKey: 'timesRedeemed',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Redemptions" />,
      cell: ({ row }) => {
        const coupon = row.original;
        return (
          <>
            {coupon.timesRedeemed}
            {' / '}
            {coupon.maxRedemptions == null ? '∞' : coupon.maxRedemptions}
          </>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
      cell: ({ row }) => formatDateTime(row.original.createdAt),
      enableSorting: true,
      meta: { className: 'whitespace-nowrap' },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <CouponStatusBadge coupon={row.original} />,
      enableSorting: false,
    },
  ];
}

const DiscountsCoupons = () => {
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort: baseSort } = useTableSort();

  const search = query.search || '';
  const status = getStatusFromQuery(query.status, FILTER_OPTIONS);

  const sortDirection = sorting[0]?.desc ? '-1' : '1';
  const sort = {
    ...baseSort,
    _id: sortDirection,
  };

  const filter = status === 'ALL' ? {} : { status: FILTER_OPTIONS[status].value };

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleStatusChange = key =>
    setParams({
      status: FILTER_OPTIONS[key].value === 'all' ? undefined : FILTER_OPTIONS[key].value,
    });

  // Filter controls
  const filterControls = (
    <div className="flex items-center gap-2 max-md:w-full [&>*:first-child]:min-w-0 max-md:[&>*:first-child]:flex-1">
      <SearchInput
        onChange={handleSearchChange}
        name="search_coupons"
        placeholder="Search by label or code"
        initialValue={search}
      />
      <FilterInput options={FILTER_OPTIONS} onChange={handleStatusChange} value={status} />
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Coupons"
        topBarActions={
          <Button to="/coupons/new" appearance="primary">
            Create coupon
          </Button>
        }
      />

      <CouponsList
        sort={sort}
        search={search}
        filter={filter}
        filterControls={filterControls}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
};

const CouponsList = ({
  sort,
  search,
  filter,
  filterControls,
  sorting: sortingProp,
  onSortingChange,
}) => {
  const { sellerId } = useAuth();
  const { getCoupons } = useCouponQueries();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: couponsKey({ search, sort, filter }),
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) =>
        await getCoupons(sellerId, {
          search,
          limit: 10,
          skip: pageParam * 10,
          ...filter,
          sort: JSON.stringify(sort),
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  const columns = useMemo(() => getCouponColumns(), []);

  const isEmpty = !isLoading && flatData.length === 0;

  let emptyState;
  if (isEmpty && search) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No coupons found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>Create your first coupon code</EmptyState.Title>
        <EmptyState.Subtitle>Offer discounts to your customers.</EmptyState.Subtitle>
        <EmptyState.Action to="/coupons/new">Create coupon</EmptyState.Action>
      </EmptyState>
    );
  }

  if (isError) {
    return <div className="m-4">Unable to load coupons. Please try again or contact support.</div>;
  }

  return (
    <DataGrid
      columns={columns}
      data={flatData}
      isLoading={isLoading}
      paginationMode="infinite"
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sorting={sortingProp}
      onSortingChange={onSortingChange}
      toolbar={
        filterControls
          ? {
              customActions: filterControls,
            }
          : undefined
      }
      getRowHref={coupon => `/coupons/${coupon._id}`}
      emptyState={emptyState}
      emptyMessage="No coupons found"
    />
  );
};

export default DiscountsCoupons;
